import React, { Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import cx from 'clsx';
import { Box, Button, makeStyles, Paper, Typography, useTheme } from '@material-ui/core';
import { ControlPointRounded } from '@material-ui/icons';
import { ProductCurriculum } from 'types/schema-generated-types';
import { getStudentCurriculumDisplayName } from 'utilities/product-utils';
import NoAvailableLicensesPopup from './NoAvailableLicensesPopup';

type AssignAllStudentsButtonProps = {
  productFamilyId: number;
  productCurriculums?: ProductCurriculum[];
  disabled?: boolean;
  onLicenseAssign: (productCurriculumId: number) => void;
};

const doesNodeTreeContainId = (
  ids: string[],
  rootNode: any,
  numberOfDescendentsToCheck: number
): boolean => {
  const doesNodeContainId = ids.includes(rootNode?.id);

  if (!doesNodeContainId && numberOfDescendentsToCheck) {
    return doesNodeTreeContainId(ids, rootNode?.parentNode, numberOfDescendentsToCheck - 1);
  }

  return doesNodeContainId;
};

const AssignAllStudentsButton = ({
  disabled = false,
  productFamilyId,
  productCurriculums = [],
  onLicenseAssign
}: AssignAllStudentsButtonProps) => {
  const classes = useStyles();
  const { t } = useTranslation('AssignAllStudentsButton');
  const theme = useTheme();
  const [showNoLicensesPopup, setShowNoLicensesPopup] = useState(false);
  const [showCurriculumSelect, setShowCurriculumSelect] = useState(false);

  const assignAllButtonId = `assign_all_license_button_${productFamilyId}`;
  const curriculumButtonIds = productCurriculums.map(x => `assign_all_curriculum_${x.id}`);

  const handleButtonClick = () => {
    if (disabled) {
      setShowNoLicensesPopup(true);
      return;
    }

    if (productCurriculums.length > 1) {
      setShowCurriculumSelect(true);
      return;
    }

    onLicenseAssign(productCurriculums[0]?.id);
  };

  const handleCurriculumClick = (productCurriculumId: number) => {
    onLicenseAssign(productCurriculumId);
    setShowCurriculumSelect(false);
  };

  useEffect(() => {
    const clickListener = (event: any) => {
      if (doesNodeTreeContainId([assignAllButtonId, ...curriculumButtonIds], event.target, 5)) {
        return;
      }

      setShowNoLicensesPopup(false);
      setShowCurriculumSelect(false);
    };

    window.addEventListener('click', clickListener);
    return () => window.removeEventListener('click', clickListener);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box className={classes.container}>
      <Button
        id={assignAllButtonId}
        aria-label="Assign all student license button"
        className={cx(classes.button, disabled && classes.buttonDisabled)}
        color="primary"
        disableElevation
        onClick={handleButtonClick}
        startIcon={<ControlPointRounded />}
        size="small"
        variant="contained"
      >
        {t('Assign licenses to all students')}
      </Button>
      {showNoLicensesPopup && (
        <NoAvailableLicensesPopup
          style={{
            left: 0,
            position: 'absolute',
            top: 50,
            width: theme.typography.pxToRem(250),
            zIndex: 100
          }}
        />
      )}
      {showCurriculumSelect && (
        <Paper className={classes.curriculumPopupContainer}>
          {productCurriculums.map(productCurriculum => (
            <Fragment key={`curriculum_button_${productCurriculum.id}`}>
              <Box>
                <Button
                  id={`assign_all_curriculum_${productCurriculum.id}`}
                  aria-label="Product Button"
                  disableElevation
                  disableRipple
                  className={classes.curriculumButton}
                  onClick={() => handleCurriculumClick(productCurriculum.id)}
                >
                  <Typography variant="body2">
                    {getStudentCurriculumDisplayName(productCurriculum)}
                  </Typography>
                </Button>
              </Box>
              <Box className={classes.popupDivider} />
            </Fragment>
          ))}
        </Paper>
      )}
    </Box>
  );
};

const useStyles = makeStyles(theme => ({
  addIcon: {
    marginRight: theme.typography.pxToRem(10)
  },
  button: {
    padding: theme.typography.pxToRem(20),
    background: '#FFFFFF',
    border: '1px solid #969696',
    color: '#2D2D2D',
    '&:hover': {
      background: '#FFFFFF'
    }
  },
  buttonDisabled: {
    backgroundColor: '#FFFFFF !important',
    boxShadow: 'none !important',
    color: 'rgba(0, 0, 0, 0.26) !important'
  },
  container: {
    display: 'inline-block',
    position: 'relative'
  },
  popupDivider: {
    borderTop: '1px solid rgba(0, 0, 0, 0.26)',
    height: theme.typography.pxToRem(1)
  },
  popupMessageContainer: {
    padding: theme.typography.pxToRem(10)
  },
  curriculumButton: {
    color: '#000000',
    borderRadius: 0,
    height: '100%',
    padding: theme.typography.pxToRem(11),
    width: '100%'
  },
  curriculumPopupContainer: {
    left: 0,
    position: 'absolute',
    top: 50,
    width: 325,
    zIndex: 100
  }
}));

export default AssignAllStudentsButton;
